import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { t, font, Button, Panel, Badge, Avatar, StatCard, ProgressBar, Timeline, ActivityItem } from "@realtyflows/ui";
import { getAgentDashboard } from "../api";
import useResponsive from "../hooks/useResponsive";
import ScoreBadge from "../components/ScoreBadge";

export default function AgentView() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const responsive = useResponsive();

  useEffect(() => {
    getAgentDashboard(agentId)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [agentId]);

  if (loading) return <div style={{ color: t.textMuted, padding: 40, textAlign: "center", fontFamily: font.body }}>Loading agent dashboard...</div>;
  if (!data) return <div style={{ color: t.textMuted, padding: 40, textAlign: "center", fontFamily: font.body }}>Agent not found</div>;

  const { agent, stats, clients, deals, activity } = data;

  return (
    <div style={{ animation: "fadeSlide 0.3s ease" }}>
      {/* Back + Agent Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
        <Button variant="secondary" size="sm" onClick={() => navigate("/team")}>&larr; Back to Team</Button>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar name={agent.name} size={44} />
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: t.textPrimary, fontFamily: font.display }}>{agent.name}</div>
            <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>{agent.email} {agent.phone ? `\u00B7 ${agent.phone}` : ""}</div>
          </div>
          <Badge variant={agent.role === "team_manager" ? "emerald" : "sky"}>{agent.role === "team_manager" ? "Manager" : "Agent"}</Badge>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ display: "grid", gridTemplateColumns: responsive.isCompact ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
        <StatCard label="Assigned Clients" value={(stats.totalClients || 0).toString()} delay="0s" />
        <StatCard label="Contacted" value={(stats.contacted || 0).toString()} delay="0.07s" />
        <StatCard label="Deals" value={(stats.totalDeals || 0).toString()} delay="0.14s" />
        <StatCard label="Closed" value={(stats.closedDeals || 0).toString()} delay="0.21s" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: responsive.settingsColumns, gap: 20, marginBottom: 24 }}>
        {/* Client Sources */}
        <Panel padding={20}>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 16, fontFamily: font.display }}>Client Sources</div>
          {stats.sources.length > 0 ? stats.sources.map(s => {
            const srcColors = { Zillow: t.emerald, "Facebook Ad": t.sky, Website: t.emeraldDeep, Referral: t.amber, Instagram: t.coral, Manual: t.textMuted };
            const pct = stats.totalClients > 0 ? Math.round((parseInt(s.count) / stats.totalClients) * 100) : 0;
            return (
              <div key={s.source} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                <div style={{ width: 28, height: 28, borderRadius: 6, background: (srcColors[s.source] || t.emerald) + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: srcColors[s.source] || t.emerald }}>
                  {s.source[0]}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                    <span style={{ fontSize: 12, color: t.textPrimary, fontFamily: font.body }}>{s.source}</span>
                    <span style={{ fontSize: 11, color: t.textMuted, fontFamily: font.mono }}>{s.count} clients ({pct}%)</span>
                  </div>
                  <ProgressBar value={pct} color={srcColors[s.source] || t.emerald} size="sm" />
                </div>
              </div>
            );
          }) : (
            <div style={{ padding: 20, textAlign: "center", color: t.textMuted, fontSize: 13, fontFamily: font.body }}>No clients assigned yet</div>
          )}
        </Panel>

        {/* Recent Activity */}
        <Panel padding={20}>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 16, fontFamily: font.display }}>Recent Activity</div>
          {activity.length > 0 ? (
            <Timeline>
              {activity.map(a => (
                <ActivityItem
                  key={a.id}
                  icon="\u25CE"
                  color={t.emerald}
                  title={a.action}
                  subtitle={a.detail}
                  timestamp={new Date(a.created_at).toLocaleDateString()}
                />
              ))}
            </Timeline>
          ) : (
            <div style={{ padding: 20, textAlign: "center", color: t.textMuted, fontSize: 13, fontFamily: font.body }}>No activity yet</div>
          )}
        </Panel>
      </div>

      {/* Clients Table */}
      <Panel padding={20} style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 16, fontFamily: font.display }}>Assigned Clients ({clients.length})</div>
        {clients.length > 0 ? (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 0.8fr 0.8fr", padding: "8px 0", borderBottom: `1px solid ${t.border}`, fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: 1, fontFamily: font.mono }}>
              <div>CONTACT</div><div>SOURCE</div><div>LOCATION</div><div>INTENT</div><div>SCORE</div>
            </div>
            {clients.map(client => (
              <div key={client.id} onClick={() => navigate(`/clients/detail?clientId=${client.id}`)}
                style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 0.8fr 0.8fr", padding: "12px 0", borderBottom: `1px solid ${t.border}`, alignItems: "center", cursor: "pointer", transition: "background 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Avatar name={client.name} size={30} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{client.name}</div>
                    <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>{client.email}</div>
                  </div>
                </div>
                <div style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: t.surfaceWarm, color: t.textMuted, fontFamily: font.mono, width: "fit-content" }}>{client.source}</div>
                <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>{client.location}</div>
                <Badge variant={client.intent === "High" ? "emerald" : client.intent === "Medium" ? "amber" : "sky"}>{client.intent}</Badge>
                <ScoreBadge score={client.score} />
              </div>
            ))}
          </>
        ) : (
          <div style={{ padding: 20, textAlign: "center", color: t.textMuted, fontSize: 13, fontFamily: font.body }}>No clients assigned to this agent</div>
        )}
      </Panel>

      {/* Deals Table */}
      <Panel padding={20}>
        <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 16, fontFamily: font.display }}>Deals ({deals.length})</div>
        {deals.length > 0 ? (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1.5fr 2fr 1fr 1fr 1fr", padding: "8px 0", borderBottom: `1px solid ${t.border}`, fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: 1, fontFamily: font.mono }}>
              <div>CONTACT</div><div>PROPERTY</div><div>BUDGET</div><div>STATUS</div><div>DATE</div>
            </div>
            {deals.map(deal => (
              <div key={deal.id} style={{ display: "grid", gridTemplateColumns: "1.5fr 2fr 1fr 1fr 1fr", padding: "12px 0", borderBottom: `1px solid ${t.border}`, alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Avatar name={deal.client_name || "?"} size={28} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{deal.client_name}</span>
                </div>
                <div style={{ fontSize: 12, color: t.textPrimary, fontFamily: font.body }}>{deal.property_address || "TBD"}</div>
                <div style={{ fontSize: 12, color: t.textPrimary, fontFamily: font.mono }}>{deal.budget || "\u2014"}</div>
                <Badge variant={deal.status === "Closed" ? "emerald" : deal.status === "Offer Submitted" ? "purple" : deal.status === "Showing Scheduled" ? "amber" : "sky"}>{deal.status}</Badge>
                <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.mono }}>{new Date(deal.created_at).toLocaleDateString()}</div>
              </div>
            ))}
          </>
        ) : (
          <div style={{ padding: 20, textAlign: "center", color: t.textMuted, fontSize: 13, fontFamily: font.body }}>No deals for this agent</div>
        )}
      </Panel>
    </div>
  );
}
